import React, { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import {
  LayoutDashboard, FileStack, QrCode, ReceiptIndianRupee, History, ScrollText,
  Settings, LogOut, Menu, X, CreditCard, MessagesSquare, Banknote, Users,
} from "lucide-react";

const NAV = [
  { to: "/admin/dashboard", label: "Dashboard", Icon: LayoutDashboard, end: true },
  { to: "/admin/customers", label: "Customer Loans", Icon: Users },
  { to: "/admin/disburse", label: "Disburse Loans", Icon: Banknote },
  { to: "/admin/chats", label: "Customer Chats", Icon: MessagesSquare },
  { to: "/admin/repayments", label: "Repayment Verification", Icon: ReceiptIndianRupee },
  { to: "/admin/payments", label: "Fee Payments", Icon: CreditCard },
  { to: "/admin/payment-settings", label: "Payment Settings", Icon: CreditCard },
  { to: "/admin/repayment-settings", label: "Repayment Settings", Icon: QrCode },
  { to: "/admin/loans", label: "Loan History", Icon: History },
  { to: "/admin/audit-logs", label: "Audit Logs", Icon: ScrollText },
  { to: "/admin/settings", label: "Admin Settings", Icon: Settings },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);

  const NavItems = ({ onClick }) => (
    <nav className="space-y-1">
      {NAV.map((n) => (
        <NavLink
          key={n.to}
          to={n.to}
          end={n.end}
          onClick={onClick}
          data-testid={`admin-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
          className={({ isActive }) =>
            `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
              isActive ? "bg-brand text-white" : "text-slate-600 hover:bg-brand-light hover:text-brand"
            }`
          }
        >
          <n.Icon className="h-4 w-4" /> {n.label}
        </NavLink>
      ))}
    </nav>
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile header */}
      <header className="lg:hidden sticky top-0 z-40 h-16 flex items-center justify-between px-4 border-b border-border bg-white">
        <Logo />
        <Button variant="outline" size="icon" onClick={() => setOpen(true)} data-testid="admin-menu-open"><Menu className="h-5 w-5" /></Button>
      </header>

      <div className="flex">
        {/* Desktop sidebar */}
        <aside className="hidden lg:flex flex-col w-64 shrink-0 border-r border-border bg-white min-h-screen p-4 sticky top-0">
          <div className="px-2 py-2 mb-4"><Logo /></div>
          <NavItems />
          <div className="mt-auto pt-4 border-t border-border">
            <p className="px-3 text-xs text-muted-foreground truncate mb-2">{user?.email}</p>
            <Button variant="outline" className="w-full" onClick={async () => { await logout(); nav("/secure-admin/login"); }} data-testid="admin-logout-button">
              <LogOut className="h-4 w-4 mr-2" /> Logout
            </Button>
          </div>
        </aside>

        {/* Mobile drawer */}
        {open && (
          <div className="lg:hidden fixed inset-0 z-50 flex">
            <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
            <div className="relative w-72 bg-white p-4 flex flex-col">
              <div className="flex items-center justify-between mb-4"><Logo /><button onClick={() => setOpen(false)}><X className="h-5 w-5" /></button></div>
              <NavItems onClick={() => setOpen(false)} />
              <div className="mt-auto pt-4 border-t border-border">
                <Button variant="outline" className="w-full" onClick={async () => { await logout(); nav("/secure-admin/login"); }}>
                  <LogOut className="h-4 w-4 mr-2" /> Logout
                </Button>
              </div>
            </div>
          </div>
        )}

        <main className="flex-1 min-w-0 p-4 sm:p-6 lg:p-8"><Outlet /></main>
      </div>
    </div>
  );
}
